/**
 * Job API Types for Portal Queue Controller
 * Based on contracts/jobs-api.yaml OpenAPI spec
 */
import type { BadgeTone } from '../components/ui'

// Enums matching backend models
export type JobStatus = 'pending' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled'

export type SourceType = 'file_upload' | 'winget' | 'chocolatey' | 'url'

export type LearnFromSource = 'winget' | 'choco'

export type ArtifactType = 'psadt_package' | 'intune_package' | 'report' | 'log' | 'full_package'

// Request types
export interface JobSubmitRequest {
  source_type: SourceType
  source_reference?: string  // WinGet package ID (e.g., "Google.Chrome")
  deployment_methods: ('psadt' | 'intune')[]
  file?: File  // For file_upload source type
  upload_id?: string  // A completed resumable upload, in place of file (file_upload source type)
  installer_entry?: string  // An uploaded zip: the path inside it of the installer to run
  app_name?: string  // The package name the user typed; overrides the name derived from the file
  target_app_id?: string  // Upload new version: the uploaded app this installer is a newer version of
  manual_params?: Record<string, unknown>  // Manual mode: the parameters the user entered (implies manual_mode)
  enable_agentic?: boolean  // Enable AI-powered analysis (default: true)
  learn_from?: LearnFromSource  // Source to learn package info from (winget or choco)
  install_timeout?: number  // Installation timeout in seconds (60-3600). Default: 600
  version?: string  // Specific version to package (e.g., '7.86.0'). Only for winget/chocolatey.
  skip_testing?: boolean  // Skip install/uninstall testing. Package will be generated without validation.
  /** The discovered app this job packages. Set by Discovery; it is what makes the job show up in
   *  Discovery's Conversions list, so leaving it off silently drops the record. */
  discovery_source?: {
    display_name: string
    publisher?: string | null
    version?: string | null
    detected_app_id?: string | null
  }
}

export interface JobListParams {
  status?: JobStatus[]
  limit?: number
  offset?: number
}

// Response types
export interface PackagingJob {
  job_id: string
  tenant_id: string
  submitted_by: string
  source_type: SourceType
  source_reference: string
  source_hash: string
  deployment_methods: string[]
  status: JobStatus
  progress_pct: number
  error_message: string | null
  result_app_id: string | null
  artifacts_path: string | null
  celery_task_id: string | null
  retry_count: number
  retry_of_job_id: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
  timeout_at: string | null
  learn_from?: LearnFromSource | null  // Package source used (winget or choco)
  installer_entry?: string | null  // An uploaded zip: the installer chosen in it
  aeam_report?: string | null  // AEAM packaging report (markdown)
  /** Set when the platform packages a failed job again with a different strategy: on the failed
   *  job it names the new job, on the new job the failed one. */
  recovery?: JobRecovery | null
  /** Set when the job's test was blocked by the test environment or a dependency and the
   *  package waits for the user's decision (Publish anyway). */
  decision?: JobDecision | null
}

export interface JobDecision {
  state: 'pending' | 'publishing' | 'accepted' | 'superseded'
  blocked_by: 'client_os' | 'prerequisite' | 'hardware' | 'licence_or_network' | 'uninstall_not_verified' | null
  reason: string | null
  /** The fixed text the user confirms before Publish anyway. */
  acknowledgement: string
  accepted_by: string | null
  accepted_at: string | null
  /** Set when blocked_by is uninstall_not_verified: what the failed uninstall test showed. */
  uninstall?: UninstallFailure | null
}

export interface UninstallFailure {
  /** The uninstall command in the package. */
  command: string | null
  exit_code: number | null
  /** What the exit code means, in plain words. */
  exit_meaning: string | null
  /** The detection rule still found the app after the uninstall. */
  still_detected: boolean
  /** What the detection rule still matched. */
  detection_match: string | null
  /** Entries still listed in Programs and Features. */
  remaining_entries: string[]
  /** Folders that still hold the app's files. */
  remaining_folders: string[]
}

export interface JobRecovery {
  message: string
  /** The recovery job's state: still running, failed too, or completed. */
  state: 'retrying' | 'failed' | 'completed'
  retry_of_job_id: string | null
  recovery_job_id: string | null
  attempt: number | null
  /** On a failed job whose recovery job failed too: that job's error. */
  recovery_error: string | null
}

export interface JobArtifact {
  artifact_id: string
  job_id: string
  artifact_type: ArtifactType
  file_name: string
  file_path: string
  file_size: number
  mime_type: string
  created_at: string
}

export interface JobSubmitResponse {
  job_id: string
  status: JobStatus
  message: string
  duplicate?: boolean
  existing_job_id?: string
  app_id?: string  // ID of the pending app created
}

export interface JobListResponse {
  jobs: PackagingJob[]
  total: number
  limit: number
  offset: number
}

export interface JobStatusResponse {
  job_id: string
  status: JobStatus
  progress_pct: number
  error_message: string | null
  updated_at: string
  result_app_id?: string | null
}

export interface BulkStatusResponse {
  [job_id: string]: JobStatusResponse
}

export interface JobArtifactsResponse {
  job_id: string
  artifacts: JobArtifact[]
}

export interface QueueMetrics {
  queue_depth: number
  active_workers: number
  processing_jobs: number
  completed_today: number
  failed_today: number
  avg_processing_time_seconds: number
  stuck_jobs: string[]  // job_ids of stuck jobs
}

// Error response
export interface JobErrorResponse {
  detail: string
  job_id?: string
  status?: JobStatus
}

// Helper type for job status display
export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  pending: 'Pending',
  queued: 'Queued',
  processing: 'Processing',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

/** Badge tone per status, as the mock's jobs page maps them (its `statusTone`): waiting states
 *  amber, running blue, done green, failed red, cancelled neutral. One map, so the row badge and
 *  the details drawer cannot disagree about what colour "processing" is. */
export const JOB_STATUS_TONES: Record<JobStatus, BadgeTone> = {
  pending: 'amber',
  queued: 'amber',
  processing: 'blue',
  completed: 'green',
  failed: 'red',
  cancelled: 'slate',
}

/** What a job is called on screen — the source it packages. The list and the details name it the
 *  same way, so one job cannot appear under two names. */
export const jobDisplayName = (
  job: Pick<PackagingJob, 'source_type' | 'source_reference' | 'learn_from'>
): string => {
  if (job.source_type === 'winget') {
    // learn_from is the source actually packaged from; winget is the default.
    return `${job.learn_from === 'choco' ? 'Chocolatey' : 'WinGet'}: ${job.source_reference}`
  }
  if (job.source_type === 'chocolatey') {
    return `Chocolatey: ${job.source_reference}`
  }
  const parts = job.source_reference.split('/')
  return parts[parts.length - 1]
}

// Check if job is in terminal state
export const isTerminalStatus = (status: JobStatus): boolean => {
  return ['completed', 'failed', 'cancelled'].includes(status)
}

// Check if job can be cancelled
export const canCancelJob = (status: JobStatus): boolean => {
  return ['pending', 'queued'].includes(status)
}

// Check if job can be stopped (forcefully terminated)
export const canStopJob = (status: JobStatus): boolean => {
  return ['pending', 'queued', 'processing'].includes(status)
}

// Check if job can be retried
export const canRetryJob = (status: JobStatus): boolean => {
  return status === 'failed'
}

/** The platform's retry of this failed job is still running: Retry is not offered (the server
 *  refuses a second retry then). */
export const retryRunning = (job: Pick<PackagingJob, 'recovery'>): boolean => {
  return Boolean(job.recovery?.recovery_job_id && job.recovery.state === 'retrying')
}

// Check if job can be deleted
export const canDeleteJob = (status: JobStatus): boolean => {
  return ['completed', 'failed', 'cancelled'].includes(status)
}
